import {
  investorProfileQueryOptions,
  useChangeSet,
  useClient,
  useInvestorInfo,
} from "creso-core/hooks";
import { useQuery } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import {
  cn,
  Form,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "web-design-system";
import { Plus, SquarePen } from "lucide-react";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { envLogger } from "@/utils/feature-flags";
import { Page } from "@core/components/layout/page";
import {
  enableUCCModification,
  findPrimaryHolder,
  findSecondHolder,
  getHolderContactInfo,
  isSecondHolderDetailsIncomplete,
} from "creso-core/utils";
import {
  AccountCenterFormSchema,
  AccountCenterFormValues,
  AccountFormProvider,
  AddressSection,
  BankDetailsSection,
  buildAccountChanges,
  ContactDetailsSection,
  NomineeSection,
  OtherInformationSection,
  PersonalInformationSection,
  useAccountFormValues,
} from "web-core";
import { Client } from "creso-core/types";
import { Route as RootRoute } from "@/routes/__root";
import { ConfirmJointAccountDialog } from "@crm/components/clients/confirm-joint-account-dialog";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";

function ProfileDetails({
  disabled,
  onEditingChange,
}: {
  disabled: boolean;
  onEditingChange: (isEditing: boolean) => void;
}) {
  const { id } = useParams({ from: "/(auth)/_headed/clients/$id" });
  const { data: client } = useClient(Number(id));
  const { data } = useInvestorInfo(Number(id));

  const router = useRouter();
  const navigate = useNavigate();

  const { modal } = useSearch({
    from: RootRoute.id,
    select: (search) => ({ modal: search?.modal }),
  });

  const openConfirmJointAccountModal = () =>
    router.navigate({
      to: ".",
      search: (prev) => ({ ...prev, modal: "confirm-joint-account" }),
    });

  const confirmJointAccountSetup = (client: Client) => {
    navigate({
      to: "/clients/$id",
      params: { id: client.id.toString() },
      search: { modal: "add-second-holder", client_id: client.id },
    });
  };

  const [isEditing, setIsEditingState] = useState(false);
  const setIsEditing = (value: boolean) => {
    setIsEditingState(value);
    onEditingChange(value);
  };

  const clientId = useMemo(() => Number(id), [id]);

  const isJointClient = Boolean(
    client?.holding_pattern &&
    JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
  );

  const { data: holderProfiles } = useQuery(
    investorProfileQueryOptions(
      { client_id: clientId },
      { enabled: isJointClient }
    )
  );

  const showHolderTabs =
    isJointClient &&
    !(
      isSecondHolderDetailsIncomplete(holderProfiles) &&
      client?.status === "AUTHENTICATION_PENDING"
    );

  const [selectedHolder, setSelectedHolder] = useState<"primary" | "second">(
    "primary"
  );

  const holderProfileId = isJointClient
    ? (selectedHolder === "second"
        ? findSecondHolder(holderProfiles)
        : findPrimaryHolder(holderProfiles)
      )?.investor_profile_guid
    : data?.profile?.investor_profile_guid;

  const [changeSet, updateChangeSet] = useChangeSet(clientId, {
    profileId: holderProfileId,
  });

  const holderData = useMemo(() => {
    if (selectedHolder === "second") {
      const secondHolder = findSecondHolder(holderProfiles);
      return {
        ...data,
        profile: secondHolder,
        contact: getHolderContactInfo(secondHolder) ?? data?.contact,
      };
    }
    if (isJointClient) {
      const primaryHolder = findPrimaryHolder(holderProfiles);
      return {
        ...data,
        profile: primaryHolder ?? data?.profile,
        contact: getHolderContactInfo(primaryHolder) ?? data?.contact,
      };
    }
    return data;
  }, [data, selectedHolder, holderProfiles, isJointClient]);

  const values = useAccountFormValues(holderData, changeSet);

  const form = useForm<AccountCenterFormValues>({
    resolver: zodResolver(AccountCenterFormSchema),
    values,
    disabled: !isEditing,
  });

  const {
    formState: { isDirty },
  } = form;

  const [isSaving, setIsSaving] = useState(false);

  const saveAll = async () => {
    try {
      setIsSaving(true);
      try {
        await updateChangeSet(buildAccountChanges(form));
        toast.success("Account details updated successfully");
        if (client?.status === "ACTIVE") {
          router.invalidate();
        }
      } catch {
        toast.error("Failed to save changes");
      }
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const context = useMemo(
    () => ({
      form,
      isEditing,
      isSaving,
      data,
      updateChangeSet,
      client: client as Client,
      onChangeSetUpdated: () => router.invalidate(),
      onReviewChanges: () =>
        router.navigate({
          to: ".",
          search: (prev) => ({ ...prev, modal: "account-updates" }),
        }),
    }),
    [form, isEditing, isSaving, data, updateChangeSet, client, router]
  );

  return (
    <div>
      <Page.Header
        className="relative"
        title={
          <>
            <h4>
              {isEditing
                ? isJointClient
                  ? `Editing ${selectedHolder} holder details`
                  : "Editing profile details"
                : "Profile details"}
            </h4>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className={cn(
                    isEditing && "invisible",
                    (!enableUCCModification(client) || disabled) &&
                      "hover:bg-transparent opacity-50"
                  )}
                  disabled={disabled}
                  onClick={() => setIsEditing(enableUCCModification(client))}
                  variant="ghost"
                  size="icon"
                  type="button"
                >
                  <SquarePen />
                </Button>
              </TooltipTrigger>
              {!enableUCCModification(client) ? (
                <TooltipContent side="bottom">
                  {client?.holding_pattern &&
                  JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
                    ? `Account Center updates from platform are not supported for joint accounts pending approval.`
                    : `Account Center updates from platform are only supported for
                  resident individuals. For others, contact support.`}
                </TooltipContent>
              ) : null}
            </Tooltip>
          </>
        }
        actions={
          <div className={cn("gap-3 flex flex-row items-center ml-auto")}>
            {showHolderTabs && !isEditing ? (
              <Tabs
                value={selectedHolder}
                onValueChange={(value) =>
                  setSelectedHolder(value as "primary" | "second")
                }
              >
                <TabsList>
                  <TabsTrigger value="primary">Primary</TabsTrigger>
                  <TabsTrigger value="second">Second</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
            {isEditing ? (
              <>
                <Button
                  onClick={() => {
                    form.reset();
                    setIsEditing(false);
                  }}
                  variant="outline"
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={form.handleSubmit(saveAll, (errors) =>
                    envLogger.warn(errors)
                  )}
                  disabled={!isDirty || isSaving}
                >
                  Save
                </Button>
              </>
            ) : client?.holding_pattern === "single" ? (
              <Button onClick={openConfirmJointAccountModal} type="button">
                <Plus className="w-4 h-4" />
                Add Second Holder
              </Button>
            ) : null}
          </div>
        }
      />
      <Page.Body className="pt-0">
        <Form {...form}>
          <AccountFormProvider value={context}>
            <PersonalInformationSection />
            <OtherInformationSection />
            <ContactDetailsSection />
          </AccountFormProvider>
        </Form>
      </Page.Body>
      {modal === "confirm-joint-account" ? (
        <ConfirmJointAccountDialog
          clientId={clientId}
          onConfirm={confirmJointAccountSetup}
        />
      ) : null}
    </div>
  );
}

function AccountDetails({
  disabled,
  onEditingChange,
}: {
  disabled: boolean;
  onEditingChange: (isEditing: boolean) => void;
}) {
  const { id } = useParams({ from: "/(auth)/_headed/clients/$id" });
  const { data: client } = useClient(Number(id));
  const { data } = useInvestorInfo(Number(id));

  const router = useRouter();

  const { edit_nominee: editNomineeId } = useSearch({
    from: "/(auth)/_headed/clients/$id/account",
  });

  const [isEditing, setIsEditingState] = useState(false);
  const setIsEditing = (value: boolean) => {
    setIsEditingState(value);
    onEditingChange(value);
  };

  const canEdit = enableUCCModification(client);

  // Deep-linked nominee edit: enter edit mode so the dialog opens editable.
  useEffect(() => {
    if (editNomineeId && client && canEdit) {
      setIsEditingState(true);
      onEditingChange(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editNomineeId, client, canEdit]);

  // Wait for edit mode (when allowed) so the dialog opens once, in update mode.
  const openNomineeId =
    editNomineeId && client && (isEditing || !canEdit)
      ? editNomineeId
      : undefined;

  const clearEditNominee = () =>
    router.navigate({
      to: ".",
      search: (prev) => ({ ...prev, edit_nominee: undefined }),
      replace: true,
    });

  const clientId = useMemo(() => Number(id), [id]);

  const [changeSet, updateChangeSet] = useChangeSet(clientId);

  const values = useAccountFormValues(data, changeSet);

  const form = useForm<AccountCenterFormValues>({
    resolver: zodResolver(AccountCenterFormSchema),
    values,
    disabled: !isEditing,
  });

  const {
    formState: { isDirty },
  } = form;

  const [isSaving, setIsSaving] = useState(false);

  const saveAll = async () => {
    try {
      setIsSaving(true);
      try {
        await updateChangeSet(buildAccountChanges(form));
        toast.success("Account details updated successfully");
        if (client?.status === "ACTIVE") {
          router.invalidate();
        }
      } catch {
        toast.error("Failed to save changes");
      }
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const context = useMemo(
    () => ({
      form,
      isEditing,
      isSaving,
      data,
      updateChangeSet,
      client: client as Client,
      onChangeSetUpdated: () => router.invalidate(),
      onReviewChanges: () =>
        router.navigate({
          to: ".",
          search: (prev) => ({ ...prev, modal: "account-updates" }),
        }),
    }),
    [form, isEditing, isSaving, data, updateChangeSet, client, router]
  );

  return (
    <div>
      <Page.Header
        className="relative"
        title={
          <>
            <h4>{isEditing ? "Editing account details" : "Account details"}</h4>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className={cn(
                    isEditing && "invisible",
                    (!enableUCCModification(client) || disabled) &&
                      "hover:bg-transparent opacity-50"
                  )}
                  disabled={disabled}
                  onClick={() => setIsEditing(enableUCCModification(client))}
                  variant="ghost"
                  size="icon"
                  type="button"
                >
                  <SquarePen />
                </Button>
              </TooltipTrigger>
              {!enableUCCModification(client) ? (
                <TooltipContent side="bottom">
                  {client?.holding_pattern &&
                  JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
                    ? `Account Center updates from platform are not supported for joint accounts pending approval.`
                    : `Account Center updates from platform are only supported for
                  resident individuals. For others, contact support.`}
                </TooltipContent>
              ) : null}
            </Tooltip>
          </>
        }
        actions={
          <div
            className={cn(
              !isEditing && "invisible",
              "gap-3 flex flex-row items-center ml-auto"
            )}
          >
            <Button
              onClick={() => {
                form.reset();
                setIsEditing(false);
              }}
              variant="outline"
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={form.handleSubmit(saveAll, (errors) =>
                envLogger.warn(errors)
              )}
              disabled={!isDirty || isSaving}
            >
              Save
            </Button>
          </div>
        }
      />

      <Page.Body className="pt-0">
        <Form {...form}>
          <AccountFormProvider value={context}>
            <AddressSection />
            <BankDetailsSection />
            <NomineeSection
              openNomineeId={openNomineeId}
              onOpenNomineeHandled={clearEditNominee}
            />
          </AccountFormProvider>
        </Form>
      </Page.Body>
    </div>
  );
}

export function Account() {
  const [activeEditSection, setActiveEditSection] = useState<
    "profile" | "account" | null
  >(null);

  return (
    <div className="flex flex-col gap-8">
      <ProfileDetails
        disabled={activeEditSection === "account"}
        onEditingChange={(isEditing) =>
          setActiveEditSection(isEditing ? "profile" : null)
        }
      />
      <AccountDetails
        disabled={activeEditSection === "profile"}
        onEditingChange={(isEditing) =>
          setActiveEditSection(isEditing ? "account" : null)
        }
      />
    </div>
  );
}
